import React, { useEffect, useMemo, useRef, useState } from 'react';

import Select, { ActionMeta, GroupBase, MultiValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import cx from 'classnames';
import classes from './Select.module.scss';
import typography from '@ds-typography';
import {
  ClearIndicator,
  Control,
  DropdownIndicator,
  Group,
  GroupHeading,
  Menu,
  MenuList,
  MultiValueContainer,
  MultiValueLabel,
  MultiValueRemove,
  NoOptionsMessage,
  Option,
  Placeholder,
  SelectContainer,
  ValueContainer
} from './components';
import { OptionType } from '../../../types';

import noop from 'lodash/noop';
import { CreatableSelectProps, MultipleSelectProps } from './types';
import { createOptionFilter, getSelectValues, valueToList } from './utils';

export type ComboboxProps = Omit<
  MultipleSelectProps & CreatableSelectProps,
  'onChange' | 'options' | 'value'
> & {
  options?: OptionType[];
  creatable?: boolean;
  value?: OptionType['value'] | OptionType['value'][];
  onValueChange?: (
    values: OptionType['value'][],
    valuesList: MultiValue<OptionType>,
    meta?: ActionMeta<OptionType>
  ) => void;
  maxSelectedLimit?: number;
  fixedOptionValues?: OptionType['value'][];
  'data-test'?: string;
};

const Combobox = ({
  options = [],
  footerActionName = '',
  onFooterActionClick = noop,
  FooterActionIcon = null,
  PrefixIcon = null,
  invalid = false,
  helperText = '',
  size = 'default',
  creatable = false,
  menuIsOpen,
  components,
  value = [],
  fixedOptionValues = [],
  onValueChange = noop,
  maxSelectedLimit = Infinity,
  maxHeight,
  maxMenuHeight,
  isClearable,
  tooltipPlacement = 'top',
  isRegexSearchEnabled,
  disableSelectAll,
  ...restProps
}: ComboboxProps) => {
  const valueList = valueToList(value);
  const Component = creatable ? CreatableSelect : Select;
  const isWithFooter = footerActionName || FooterActionIcon;
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);

  const selectedValues = getSelectValues(
    [...fixedOptionValues, ...valueList],
    options
  );

  const onMenuInputFocus = (v: boolean) => {
    setIsFocused(v);
  };

  const onCloseMenu = () => {
    setIsFocused(false);
    setInputValue('');
  };

  const onDomClick = (e: MouseEvent) => {
    if (!containerRef.current?.contains(e.target as Node)) {
      onCloseMenu();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', onDomClick);

    return () => {
      document.removeEventListener('mousedown', onDomClick);
    };
  }, [isFocused]);

  const handleChange = (
    newValues: MultiValue<OptionType>,
    actionMeta: ActionMeta<OptionType>
  ) => {
    const values = newValues.map(item => item.value);
    const newOptions = getSelectValues(
      [...fixedOptionValues, ...values],
      [...newValues]
    );

    const valuesList = newOptions.map(v => v.value);
    onValueChange(valuesList, newOptions, actionMeta);
  };

  const selectedValuesLength = selectedValues.length;
  const selectionLimitReached = selectedValuesLength >= maxSelectedLimit;
  const hasEditableValue = Boolean(
    valueList?.filter(v => !fixedOptionValues.includes(v)).length
  );

  const isComboboxClearable = hasEditableValue ? isClearable : false;

  const filterOption = createOptionFilter(Boolean(isRegexSearchEnabled));

  const filteredOptions = useMemo(() => {
    if (!inputValue) return options;

    return options
      .map(group => {
        if ('options' in group && group.options) {
          const filteredGroupOptions = group.options.filter(opt =>
            opt.label.toLowerCase().includes(inputValue.toLowerCase())
          );

          return filteredGroupOptions.length > 0
            ? { ...group, options: filteredGroupOptions }
            : null;
        } else {
          return group.label.toLowerCase().includes(inputValue.toLowerCase())
            ? group
            : null;
        }
      })
      .filter(Boolean);
  }, [options, inputValue]);

  const getAllOptionValues = (
    options: (OptionType | null)[]
  ): OptionType['value'][] => {
    return options
      .filter((option): option is OptionType => option !== null)
      .flatMap(option => {
        if ('options' in option && option.options) {
          // This is a grouped option
          return option.options.map(opt => opt.value);
        }
        // This is a flat option
        return [option.value];
      });
  };

  const onSelectAll = () => {
    const relevantOptions = inputValue ? filteredOptions : options;
    const allValues = getAllOptionValues(relevantOptions);
    const selectedSet = new Set(valueList);
    const allSelected = allValues.every(val => selectedSet.has(val));

    if (allSelected) {
      onValueChange([...fixedOptionValues], []);
    } else {
      const allSelectedOptions = getSelectValues(
        [...fixedOptionValues, ...selectedSet, ...allValues],
        options
      );
      onValueChange(
        allSelectedOptions.map(o => o.value),
        allSelectedOptions
      );
    }
  };

  const hasSelectedAll = useMemo(() => {
    const allValues = options.map(o => o.value);
    return allValues.every(v => valueList.includes(v));
  }, [options, valueList]);

  return (
    <div ref={containerRef}>
      <Component<OptionType, true, GroupBase<OptionType>>
        options={options}
        components={{
          Option,
          DropdownIndicator,
          MenuList,
          Menu,
          GroupHeading,
          Group,
          Control,
          ClearIndicator,
          MultiValueContainer,
          MultiValueRemove,
          MultiValueLabel,
          NoOptionsMessage,
          SelectContainer,
          ValueContainer,
          Placeholder,
          ...components
        }}
        classNames={{
          control: state =>
            cx(
              classes.controlContainer,
              typography.dsBody,
              classes[size],
              classes.multiControl,
              {
                [classes.open]: state.menuIsOpen,
                [classes.disabledControl]: state.isDisabled,
                [classes.invalidControl]: invalid,
                [classes.noValueControl]: !state.hasValue
              }
            ),
          placeholder: () => classes.placeholder,
          indicatorSeparator: () => classes.decoratorSeparator,
          dropdownIndicator: state =>
            cx(classes.dropdownIndicator, {
              [classes.disabledDropdownIndicator]: state.isDisabled
            }),
          menuList: () =>
            cx(classes.comboboxMenuList, {
              [classes.menuWithoutBottomPadding]: isWithFooter
            }),
          multiValue: data =>
            cx(classes.multiValueContainer, classes[size], {
              [classes.invalidMultiValue]: data.data.isInvalid,
              [classes.isFixed]: data.selectProps.fixedOptionValues?.includes(
                data.data.value
              ),
              [classes.disabledMultiValue]:
                data.data.isDisabled || data.selectProps.isDisabled
            }),
          multiValueLabel: () => classes.multiValueLabel,
          indicatorsContainer: () => classes.multiIndicatorsContainer,
          noOptionsMessage: () => classes.noOptionsMessage,
          menuPortal: () => classes.selectMenuPortal
        }}
        unstyled
        PrefixIcon={PrefixIcon}
        footerActionName={footerActionName}
        FooterActionIcon={FooterActionIcon}
        onFooterActionClick={onFooterActionClick}
        invalid={invalid}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        captureMenuScroll={!maxHeight}
        menuPortalTarget={
          containerRef.current?.closest('[role="presentation"]') ||
          document.body
        }
        tooltipPlacement={tooltipPlacement}
        {...restProps}
        maxMenuHeight={maxMenuHeight || maxHeight}
        filterOption={filterOption}
        isClearable={isComboboxClearable}
        maxHeight={maxHeight}
        isOptionDisabled={() => selectionLimitReached}
        isSearchable={false}
        selectionLimitReached={selectionLimitReached}
        fixedOptionValues={fixedOptionValues}
        onChange={handleChange}
        value={selectedValues}
        menuPosition="fixed"
        onMenuInputFocus={onMenuInputFocus}
        placeholder="Select..."
        autoFocus={false}
        isFocused={isFocused || undefined}
        menuIsOpen={isFocused || menuIsOpen}
        menuPlacement="auto"
        inputValue={inputValue}
        onInputChange={(value, { action }) => {
          if (action !== 'set-value') {
            setInputValue(value);
          }
        }}
        onMenuClose={onCloseMenu}
        classNamePrefix="design-system-select"
        isRegexSearchEnabled={isRegexSearchEnabled}
        withSearch={true}
        searchPlaceholder={undefined}
        isMulti
        handleSelectAll={onSelectAll}
        hasSelectedAll={hasSelectedAll}
        disableSelectAll={filteredOptions?.length === 0 || disableSelectAll}
      />
      {helperText && (
        <div
          className={cx(typography.dsBodySmall, classes.helperTextContainer, {
            [classes.invalidHelperText]: invalid
          })}
        >
          {helperText}
        </div>
      )}
    </div>
  );
};

export default Combobox;
